import React, { useEffect, useState } from 'react'
import client from '../../client/Client'
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
} from '@material-tailwind/react'
import HeaderBanner from '../ui/HeaderBanner'
import SideBar from '../utils/SideBar'

interface BiographyBlock {
  _key?: string
  children: Array<{
    text: string
  }>
}

interface CommitteeMember {
  MemberName: string
  Coutry: string
  University?: string
  LinkedIn?: string
  Biography?: BiographyBlock[]
  image?: {
    asset?: {
      _ref?: string
    }
  }
}

interface OrganizingCommitee {
  LocalORGCOM: CommitteeMember[]
  IntORGCOM: CommitteeMember[]
}

const OrganizingCommittee = () => {
  const [orgCommittee, setOrgCommittee] = useState<OrganizingCommitee>({
    LocalORGCOM: [],
    IntORGCOM: [],
  })

  const [open, setOpen] = useState(0)
  const [selectedMember, setSelectedMember] = useState<CommitteeMember | null>(
    null
  )

  const handleOpen = (value: number) => {
    setOpen(open === value ? 0 : value)
  }

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    })
  }, [])

  useEffect(() => {
    client
      .fetch('*[_type == "OrgCommitees"]')
      .then((result) => {
        if (result.length > 0) {
          setOrgCommittee(result[0])
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })
  }, [])

  useEffect(() => {
    if (selectedMember) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflowY = 'hidden'
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1)
      }
    }
  }, [selectedMember])

  const getImageUrl = (ref?: string) => {
    if (!ref) return ''

    return `https://cdn.sanity.io/images/79vgbaej/production/${ref.slice(
      6,
      -4
    )}.${ref.slice(-3)}`
  }

  const hasBiography = (member: CommitteeMember) => {
    return (
      Array.isArray(member.Biography) &&
      member.Biography.some((block) =>
        block?.children?.some((child) => child?.text?.trim() !== '')
      )
    )
  }

  const LinkedInIcon = () => (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.049c.476-.9 1.637-1.852 3.368-1.852 3.6 0 4.265 2.37 4.265 5.455v6.288zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.114 20.452H3.559V9h3.555v11.452z" />
    </svg>
  )

  const BiographyIcon = () => (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9 9 0 100-18 9 9 0 000 18z"
      />
    </svg>
  )

  const renderMobileMemberCards = (members: CommitteeMember[]) => {
    return (
      <div className="space-y-4 md:hidden">
        {members.map((item, index) => {
          const imageUrl = getImageUrl(item.image?.asset?._ref)
          const linkedinUrl = item.LinkedIn?.trim()
          const memberHasBiography = hasBiography(item)

          return (
            <article
              key={`${item.MemberName}-mobile-${index}`}
              className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:border-red-100 hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={item.MemberName}
                    className="h-20 w-20 shrink-0 rounded-xl border border-gray-100 object-cover shadow-sm"
                  />
                ) : (
                  <div className="h-20 w-20 shrink-0 rounded-xl border border-gray-100 bg-gray-50" />
                )}

                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold leading-snug text-gray-900">
                    {item.MemberName}
                  </h3>

                  {(linkedinUrl || memberHasBiography) && (
                    <div className="mt-3 flex items-center gap-2">
                      {linkedinUrl && (
                        <a
                          href={linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${item.MemberName} LinkedIn profile`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#00094f]/10 bg-[#00094f]/5 text-[#00094f] transition-all duration-200 hover:border-[#00094f] hover:bg-[#00094f] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#00094f]/30 focus:ring-offset-2"
                        >
                          <LinkedInIcon />
                        </a>
                      )}

                      {memberHasBiography && (
                        <button
                          type="button"
                          onClick={() => setSelectedMember(item)}
                          aria-label={`Open biography for ${item.MemberName}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-500 transition-all duration-200 hover:border-red-500 hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                        >
                          <BiographyIcon />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {item.University && (
                  <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-red-500">
                      Affiliation
                    </p>
                    <p className="mt-1 text-sm font-medium leading-6 text-gray-700">
                      {item.University}
                    </p>
                  </div>
                )}

                {item.Coutry && (
                  <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-red-500">
                      Country
                    </p>
                    <p className="mt-1 text-sm font-medium leading-6 text-gray-700">
                      {item.Coutry}
                    </p>
                  </div>
                )}
              </div>
            </article>
          )
        })}
      </div>
    )
  }

  const renderDesktopTable = (members: CommitteeMember[]) => {
    return (
      <div className="hidden overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm md:block">
        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-red-500 sm:text-sm"
                >
                  Member
                </th>

                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-red-500 sm:text-sm"
                >
                  Affiliation
                </th>

                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-red-500 sm:text-sm"
                >
                  Country
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {members.map((item, index) => {
                const imageUrl = getImageUrl(item.image?.asset?._ref)
                const linkedinUrl = item.LinkedIn?.trim()
                const memberHasBiography = hasBiography(item)

                return (
                  <tr
                    key={`${item.MemberName}-${index}`}
                    className="transition-colors duration-200 hover:bg-red-50/40"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-4">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.MemberName}
                            className="h-20 w-20 shrink-0 rounded-xl border border-gray-100 object-cover shadow-sm sm:h-24 sm:w-24"
                          />
                        ) : (
                          <div className="h-20 w-20 shrink-0 rounded-xl border border-gray-100 bg-gray-50 sm:h-24 sm:w-24" />
                        )}

                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 sm:text-base">
                            {item.MemberName}
                          </h3>

                          {(linkedinUrl || memberHasBiography) && (
                            <div className="mt-3 flex items-center gap-2">
                              {linkedinUrl && (
                                <a
                                  href={linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`${item.MemberName} LinkedIn profile`}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#00094f]/10 bg-[#00094f]/5 text-[#00094f] transition-all duration-200 hover:border-[#00094f] hover:bg-[#00094f] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#00094f]/30 focus:ring-offset-2"
                                >
                                  <LinkedInIcon />
                                </a>
                              )}

                              {memberHasBiography && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedMember(item)}
                                  aria-label={`Open biography for ${item.MemberName}`}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-500 transition-all duration-200 hover:border-red-500 hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                                >
                                  <BiographyIcon />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 text-sm font-medium text-gray-600 sm:text-base">
                      {item.University}
                    </td>

                    <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-600 sm:text-base">
                      {item.Coutry}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderCommitteeTable = (members: CommitteeMember[]) => {
    if (!members || members.length === 0) {
      return (
        <div className="rounded-xl border border-red-100 bg-red-50 p-5 text-center">
          <p className="text-base font-bold text-red-500">Coming Soon</p>
        </div>
      )
    }

    return (
      <>
        {renderMobileMemberCards(members)}
        {renderDesktopTable(members)}
      </>
    )
  }

  const renderAccordionSection = (
    id: number,
    title: string,
    members: CommitteeMember[]
  ) => {
    const isOpen = open === id

    return (
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:border-red-100 hover:shadow-md">
        <Accordion open={isOpen}>
          <AccordionHeader
            onClick={() => handleOpen(id)}
            className="border-0 px-0 py-0 hover:text-red-500"
          >
            <div className="flex w-full items-center justify-between gap-4 px-4 py-4 sm:px-5">
              <div className="flex items-center gap-3">
                <span
                  className={`h-10 w-1.5 rounded-full transition-colors duration-300 ${
                    isOpen ? 'bg-red-500' : 'bg-[#00094f]'
                  }`}
                />

                <h2 className="text-left text-base font-bold text-red-500 sm:text-lg">
                  {title}
                </h2>
              </div>

              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                  isOpen
                    ? 'border-red-100 bg-red-50 text-red-500'
                    : 'border-gray-200 bg-gray-50 text-[#00094f]'
                }`}
              >
                <svg
                  className={`h-4 w-4 transition-transform duration-300 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </AccordionHeader>

          <AccordionBody className="border-t border-gray-100 px-4 pb-5 pt-4 sm:px-5">
            {renderCommitteeTable(members)}
          </AccordionBody>
        </Accordion>
      </div>
    )
  }

  const selectedImage = getImageUrl(selectedMember?.image?.asset?._ref)

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
      <div
        className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-1"
        aria-hidden="true"
      >
        <svg
          width="1360"
          height="578"
          viewBox="0 0 1360 578"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              x1="50%"
              y1="0%"
              x2="50%"
              y2="100%"
              id="illustration-01"
            >
              <stop stopColor="#FFF" offset="0%" />
              <stop stopColor="#EAEAEA" offset="77.402%" />
              <stop stopColor="#DFDFDF" offset="100%" />
            </linearGradient>
          </defs>

          <g fill="url(#illustration-01)" fillRule="evenodd">
            <circle cx="1232" cy="128" r="128" />
            <circle cx="155" cy="443" r="64" />
          </g>
        </svg>
      </div>

      <HeaderBanner title="Organizing Committee" />

      <div className="space-y-5 pt-12">
        {renderAccordionSection(
          1,
          'Local Organizing Committee',
          orgCommittee.LocalORGCOM
        )}

        {renderAccordionSection(
          2,
          'International Organizing Committee',
          orgCommittee.IntORGCOM
        )}
      </div>

      <SideBar
        isOpen={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        name={selectedMember?.MemberName || ''}
        url={selectedMember?.Coutry || ''}
        image={selectedImage}
        biography={selectedMember?.Biography}
      />
    </div>
  )
}

export default OrganizingCommittee